/* ============================================================================
   ACEPLORE · module-fbt.js — <lumina-fbt> "Usually bought with" widget.

   The section ships an initial shell containing the current product ("This
   item") + summary, plus an empty [data-fbt-rec-slot]. recommendations.performed?
   is false on first load, so we fetch the section through the Product
   Recommendations API (which re-renders it with ONLY the recommendation cards,
   independent of the current `product`), then inject those cards into the slot
   and wire up the running total + add-all button.

   Complementary products must be configured in Search & Discovery; if that
   intent is empty we retry once with the auto-generated "related" intent. If
   still empty (nothing to cross-sell), the whole section is removed.
   ========================================================================== */
(function () {
  'use strict';

  var Lumina = (window.Lumina = window.Lumina || {});
  var utils = Lumina.utils || {};
  var Base = Lumina.Element || window.HTMLElement;

  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  class LuminaFbt extends Base {
    connectedCallback() {
      this.url = this.dataset.url || '';
      this.productId = this.dataset.productId || '';
      this.sectionId = this.dataset.sectionId || '';
      this.intent = this.dataset.intent || 'complementary';
      this.limit = parseInt(this.dataset.limit, 10) || 3;
      this.triedRelated = false;
      this.loaded = false;

      // `HTMLElement.slot` is a native string property. Using `this.slot` here
      // stringified the element and made insertAdjacentHTML crash at runtime.
      this.recSlot = qs('[data-fbt-rec-slot]', this);
      this.currentCb = qs('[data-fbt-current]', this);
      this.currentPriceEl = qs('[data-fbt-current-price]', this);
      var mainForm = qsa('lumina-product-form[data-section]').find(function (form) { return !form.closest('lumina-quickview'); });
      this.productSection = mainForm ? mainForm.dataset.section : '';
      if (this.sub) this.sub('variant:change', function (detail) {
        if (this.productSection && detail && detail.sectionId !== this.productSection) return;
        this.syncCurrentVariant(detail);
      }.bind(this));
      // Only the initial shell carries the slot. Anything else (e.g. an empty
      // render) is left alone.
      if (!this.recSlot) return;
      // Theme editor: recommendations can't be computed offline — leave the shell.
      if (window.Shopify && window.Shopify.designMode) return;
      // No product context / route — a lone "This item" is pointless; drop it.
      if (!this.url || !this.productId) { this.dropSection(); return; }

      this.startObserving();
    }

    dropSection() {
      var host = this.closest('[data-section-type="fbt"]') || this;
      host.remove();
    }

    startObserving() {
      if (!('IntersectionObserver' in window)) { this.load(); return; }
      this._io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { this.disconnectObserver(); this.load(); }
        }.bind(this));
      }.bind(this), { rootMargin: '500px 0px' });
      this._io.observe(this);
      if (this._cleanups) this._cleanups.push(this.disconnectObserver.bind(this));
    }

    disconnectObserver() { if (this._io) { this._io.disconnect(); this._io = null; } }

    syncCurrentVariant(detail) {
      var variant = detail && detail.variant;
      if (!this.currentCb) return;
      var available = Boolean(variant && variant.available);
      this.currentCb.checked = available;
      this.currentCb.dataset.variant = available ? String(variant.id) : '';
      this.currentCb.dataset.price = available ? String(variant.price || 0) : '0';
      if (this.currentPriceEl && utils.formatMoney) {
        this.currentPriceEl.textContent = available ? utils.formatMoney(variant.price || 0) : (Lumina.config?.strings?.soldOut || 'Unavailable');
      }
      if (this.cbs) this.recompute();
      else {
        var total = qs('[data-fbt-total]', this);
        var count = qs('[data-fbt-count]', this);
        if (total && utils.formatMoney) total.textContent = utils.formatMoney(available ? variant.price : 0);
        if (count) count.textContent = available ? '1' : '0';
      }
    }

    buildUrl(intent) {
      var u = new URL(this.url, window.location.origin);
      u.searchParams.set('section_id', this.sectionId);
      u.searchParams.set('product_id', this.productId);
      u.searchParams.set('limit', String(this.limit));
      u.searchParams.set('intent', intent);
      return u.toString();
    }

    async load() {
      if (this.loaded) return;
      this.loaded = true;
      this.setAttribute('aria-busy', 'true');
      try {
        await this.fetchIntent(this.intent);
      } catch (err) {
        if (window.console && console.warn) console.warn('[Lumina] fbt', err);
        this.dropSection();
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    async fetchIntent(intent) {
      var res = await fetch(this.buildUrl(intent));
      if (!res.ok) throw new Error('FBT request failed (' + res.status + ')');
      var html = await res.text();
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector('lumina-fbt');
      var box = fresh ? fresh.querySelector('[data-fbt-recs]') : null;
      var cards = box ? box.querySelectorAll('[data-fbt-rec]') : [];

      if (box && cards.length && this.recSlot) {
        // Insert the recommendation cards (with their "+" connectors) before the slot.
        this.recSlot.insertAdjacentHTML('beforebegin', box.innerHTML);
        this.recSlot.parentNode.removeChild(this.recSlot);
        this.recSlot = null;
        this.bind();
        // Never leave injected cards invisible if the reveal system is absent.
        if (Lumina.initReveal) Lumina.initReveal(this);
        else qsa('.lm-reveal', this).forEach(function (el) { el.classList.add('is-visible'); });
        if (Lumina.events) Lumina.events.emit('section:load', { id: this.sectionId, target: this });
        return;
      }
      // Empty complementary set → fall back to the always-available related set.
      if (intent === 'complementary' && !this.triedRelated) {
        this.triedRelated = true;
        await this.fetchIntent('related');
        return;
      }
      this.dropSection();
    }

    bind() {
      this.cbs = qsa('[data-fbt-cb]', this);
      this.totalEl = qs('[data-fbt-total]', this);
      this.countEl = qs('[data-fbt-count]', this);
      this.addBtn = qs('[data-fbt-add]', this);
      this.label = qs('[data-fbt-label]', this);
      if (!this.cbs.length || !this.addBtn) return;

      this.cbs.forEach(function (cb) {
        if (cb.disabled) return; // the current item is locked-in
        var item = cb.closest('.lm-fbt__item');
        this.listen(cb, 'change', function () {
          if (item) item.classList.toggle('is-deselected', !cb.checked);
          this.recompute();
        }.bind(this));
      }.bind(this));

      qsa('[data-fbt-variant]', this).forEach(function (select) {
        this.listen(select, 'change', function () {
          var option = select.options[select.selectedIndex];
          var item = select.closest('[data-fbt-rec]');
          var cb = item ? qs('[data-fbt-cb]', item) : null;
          var price = item ? qs('[data-fbt-rec-price]', item) : null;
          if (!option || !cb) return;
          cb.dataset.variant = option.value;
          cb.dataset.price = option.dataset.price || '0';
          if (price && utils.formatMoney) price.textContent = utils.formatMoney(parseInt(cb.dataset.price, 10) || 0);
          this.recompute();
        }.bind(this));
      }.bind(this));

      this.listen(this.addBtn, 'click', this.addSelected.bind(this));
      this.recompute();
    }

    recompute() {
      var cents = 0;
      var count = 0;
      this.cbs.forEach(function (cb) {
        if (cb.checked) { cents += parseInt(cb.dataset.price, 10) || 0; count += 1; }
      });
      if (this.totalEl) this.totalEl.textContent = utils.formatMoney ? utils.formatMoney(cents) : cents;
      if (this.countEl) this.countEl.textContent = String(count);
      this.addBtn.disabled = count === 0;
    }

    async addSelected() {
      var ids = this.cbs
        .filter(function (cb) { return cb.checked; })
        .map(function (cb) { return Number(cb.dataset.variant); })
        .filter(Boolean);
      if (!ids.length) return;

      var strings = (Lumina.config && Lumina.config.strings) || {};
      var original = this.label ? this.label.textContent : '';
      this.addBtn.disabled = true;
      this.addBtn.classList.add('is-loading');
      var spinner = document.createElement('span');
      spinner.className = 'lm-spinner';
      this.addBtn.prepend(spinner);

      try {
        var items = ids.map(function (id) { return { id: id, quantity: 1 }; });
        await Lumina.addToCart(items);
        if (this.label) this.label.textContent = strings.added || 'Added';
        spinner.remove();
        var btn = this.addBtn;
        var label = this.label;
        setTimeout(function () {
          if (label) label.textContent = original;
          btn.disabled = false;
          btn.classList.remove('is-loading');
        }, 1500);
      } catch (err) {
        spinner.remove();
        if (this.label) this.label.textContent = original;
        this.addBtn.disabled = false;
        this.addBtn.classList.remove('is-loading');
        if (Lumina.toast) Lumina.toast((err && err.message) || strings.cartError || 'Could not add to cart', { type: 'error' });
      }
    }
  }

  if (!customElements.get('lumina-fbt')) {
    customElements.define('lumina-fbt', LuminaFbt);
  }
})();
